/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA DIRECCIÓN QUE SE LE ENSEÑA A UNA PERSONA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Google devuelve el `formattedAddress` con dos añadidos que no le dicen nada
 * a nadie en un campo de recogida o de destino:
 *
 *   - al principio, donde no hay número de casa (casi toda la periferia), un
 *     Plus Code: «C8MM+88 Andrés»;
 *   - al final, el país: «…, Santo Domingo Este, República Dominicana».
 *
 * Esto vivía suelto dentro de `map.tsx`, aplicado SOLO a la recogida
 * automática. Resultado medido el 16-oct-2026: quien dejaba la recogida como
 * venía veía la dirección limpia, y quien ELEGÍA una sugerencia de Google
 * —mismo campo, misma pantalla— veía el Plus Code y el país. Y lo mismo en el
 * destino y en «mis direcciones».
 *
 * Por eso está aquí: los cuatro sitios que pintan una dirección de Google usan
 * esta función, tres de ellos a través de `PlacesAutocomplete`.
 *
 * ── DÓNDE *NO* SE USA, Y POR QUÉ IMPORTA ─────────────────────────────────
 *
 * No se aplica a una dirección escrita a mano, sin coordenadas. Ese texto lo
 * vuelve a resolver el servidor (`resolverDestinoEscrito`), y ahí el Plus Code
 * es justo lo más preciso que hay: quitárselo cambiaría «C8MM+88 Andrés» por
 * «Andrés» y el punto caería a kilómetros. Con coordenadas no hay nada que
 * resolver, así que limpiar es gratis.
 *
 * ── AL AÑADIR OTRO PAÍS ──────────────────────────────────────────────────
 *
 * El país se quita por su nombre, a propósito, y no «quitando lo que va tras la
 * última coma»: esa regla se comería la ciudad el día que Google devuelva una
 * dirección sin país. Cuando la app opere en otro sitio, el nombre nuevo se
 * añade a PAISES y se acabó.
 */

/** Los países donde opera la app, tal como los escribe Google al final. */
const PAISES = ['República Dominicana'];

const PLUS_CODE = /^[A-Z0-9]{4,6}\+[A-Z0-9]{2,3},?\s*/i;
const AL_FINAL = new RegExp(`,\\s*(?:${PAISES.join('|')})\\s*$`, 'i');

/**
 * Deja la dirección en lo que sirve para reconocer el sitio. Devuelve cadena
 * vacía solo si entra una cadena vacía; si al limpiar no queda nada, devuelve
 * el texto original antes que un campo en blanco.
 */
export function direccionCorta(
  texto: string | null | undefined,
  opciones: { conservarPlusCode?: boolean } = {}
): string {
  const entra = String(texto ?? '').trim();
  if (!entra) return '';
  // ── CUANDO EL PLUS CODE ES LO UNICO ESPECIFICO QUE HAY ─────────────────
  // Reportado por el fundador el 18-oct-2026: en La Vega la recogida salia
  // «La Vega 41000», ciudad y codigo postal. Donde Google no tiene NINGUNA
  // calle para ese punto, su respuesta es el Plus Code — y quitarselo deja un
  // texto que no sirve para recoger a nadie.
  //
  // Es la misma razon que ya estaba escrita arriba para la direccion tecleada
  // a mano, aplicada al otro caso: feo y preciso vale mas que limpio y vago.
  // El pais se quita igual, que ese nunca aporta.
  const limpia = opciones.conservarPlusCode
    ? entra.replace(AL_FINAL, '').trim()
    : entra.replace(PLUS_CODE, '').replace(AL_FINAL, '').trim();
  return limpia || entra;
}
